/**
 * The pitch set, and the mapping from path length to note.
 *
 * Major pentatonic: no semitone clashes and no tritone, so any two notes in
 * the set sound consonant together. That is the whole point — the player
 * drives the melody with arbitrary drags and backtracks, and no sequence of
 * moves can produce a wrong-sounding interval. Never widen this to a
 * seven-note scale without accepting that guarantee is gone.
 *
 * Pure math, no Web Audio: this module is the one part of the audio system
 * that can be reasoned about (and swapped for a different mode) without a
 * running AudioContext.
 */

/**
 * Semitone offsets of the MINOR pentatonic within one octave.
 *
 * Minor rather than major because the game's voice is cinematic-space, and
 * the major pentatonic reads as pastoral/folk against that. Crucially this
 * costs nothing structurally: the minor pentatonic is the same interval set
 * as the major one seen from its relative root, so it still contains no
 * semitone and no tritone, and the "no sequence of moves can sound wrong"
 * guarantee above holds exactly as before.
 */
const DEGREES = [0, 3, 5, 7, 10] as const;

/**
 * C4. Deliberately mid-register: the scale spans up to C6, which keeps every
 * note inside the band a phone speaker can actually reproduce (small drivers
 * roll off hard below ~300 Hz).
 */
const ROOT_HZ = 261.6256;

const OCTAVES = 2;

/**
 * Two octaves plus the closing root: C4 Eb4 F4 G4 Bb4 C5 Eb5 F5 G5 Bb5 C6.
 * Ending on the octave root means each wrap of the scale lands as a
 * resolution rather than an arbitrary cut.
 */
export const SCALE_LENGTH = DEGREES.length * OCTAVES + 1;

/**
 * Frequency for a scale index, wrapping to stay inside the two-octave span.
 *
 * Paths are longer than the scale (a 9x9 board is up to 81 tiles), so the
 * index has to fold. Wrapping — rather than compressing the step size to fit
 * the path length — is what keeps every single move a real melodic step:
 * compression would map seven consecutive tiles onto the same pitch on a big
 * board, and the ear reads that as "nothing happened". The cost is one
 * octave-drop per 11 tiles, and that drop lands on the root, so it reads as
 * the start of a new phrase.
 *
 * Negative indices fold the same way, which is what makes backtracking an
 * exact mirror of the forward path.
 */
export function frequencyAt(index: number): number {
  const wrapped = ((index % SCALE_LENGTH) + SCALE_LENGTH) % SCALE_LENGTH;
  const octave = Math.floor(wrapped / DEGREES.length);
  const degree = wrapped % DEGREES.length;
  return ROOT_HZ * 2 ** ((octave * 12 + DEGREES[degree]) / 12);
}

/**
 * The note for a path of `length` tiles. Length 1 (the start tile) is the
 * root, so every level opens on the tonic.
 */
export function indexForPathLength(length: number): number {
  return length - 1;
}

/** Every distinct pitch in the scale — what a sound pack pre-renders. */
export function scaleFrequencies(): number[] {
  return Array.from({ length: SCALE_LENGTH }, (_, i) => frequencyAt(i));
}

/** Rising figure played on a win: C4 Eb4 F4 G4 C5 Eb5 — the minor-pentatonic climb. */
export const WIN_ARPEGGIO = [0, 1, 2, 3, 5, 6] as const;

/**
 * The chord it settles on: root, fifth, octave, and the octave's fifth.
 * Deliberately no third — an open fifth stack reads as vast rather than
 * as either happy or sad, which is the cinematic-space register the rest
 * of the audio is aiming at.
 */
export const WIN_CHORD = [0, 3, 5, 8, 10] as const;
